import type { LifeStage } from "@ferrivia/api-contract";
import { useTranslation } from "@ferrivia/i18n";
import { ChoiceRow } from "../../components/ChoiceRow";
import { StepLayout } from "../components/StepLayout";
import s from "./LifeStageStep.module.css";

export interface LifeStageStepProps {
  value: LifeStage[];
  onChange: (next: LifeStage[]) => void;
  none: boolean;
  onNoneChange: (none: boolean) => void;
}

// Glide's eight options plus "menstruating", in the questionnaire's order.
const LIFE_STAGES: LifeStage[] = [
  "menstruating",
  "planning_pregnancy",
  "trying_to_conceive",
  "fertility_treatment",
  "pregnant",
  "postpartum",
  "perimenopause",
  "menopause",
  "postmenopause",
];

/**
 * The one multi-select step. "None of these apply" is stored as an empty
 * selection, so it lives outside `value` and is exclusive with every stage.
 */
export function LifeStageStep({ value, onChange, none, onNoneChange }: LifeStageStepProps) {
  const { t } = useTranslation("client");

  function toggle(stage: LifeStage) {
    onNoneChange(false);
    onChange(value.includes(stage) ? value.filter((v) => v !== stage) : [...value, stage]);
  }

  function toggleNone() {
    onChange([]);
    onNoneChange(!none);
  }

  // i18next-parser only extracts literal-key, literal-default t() calls, so
  // this is a static lookup rather than a template key — same pattern as
  // TabBar's nav labels.
  const optionLabels: Record<LifeStage, string> = {
    menstruating: t("onboarding.lifeStage.options.menstruating", "Minulla on kuukautiset"),
    planning_pregnancy: t(
      "onboarding.lifeStage.options.planning_pregnancy",
      "Suunnittelen perheenlisäystä lähitulevaisuudessa",
    ),
    trying_to_conceive: t("onboarding.lifeStage.options.trying_to_conceive", "Yritän tulla raskaaksi"),
    fertility_treatment: t("onboarding.lifeStage.options.fertility_treatment", "Olen hedelmöityshoidoissa"),
    pregnant: t("onboarding.lifeStage.options.pregnant", "Olen raskaana"),
    postpartum: t("onboarding.lifeStage.options.postpartum", "Olen synnyttänyt alle vuosi sitten"),
    perimenopause: t("onboarding.lifeStage.options.perimenopause", "Vaihdevuodet ovat alkamassa"),
    menopause: t("onboarding.lifeStage.options.menopause", "Olen vaihdevuosissa"),
    postmenopause: t(
      "onboarding.lifeStage.options.postmenopause",
      "Vaihdevuodet ovat ohi",
    ),
  };

  return (
    <StepLayout
      title={t("onboarding.lifeStage.title", "Missä elämänvaiheessa olet?")}
      subtitle={t(
        "onboarding.lifeStage.subtitle",
        "Valitse kaikki vaihtoehdot, jotka koskevat sinua tällä hetkellä.",
      )}
      why={t(
        "onboarding.lifeStage.why",
        "Elämänvaiheesi vaikuttaa siihen, paljonko tarvitset rautaa. Raskaus ja imetys lisäävät raudan tarvetta, ja esivaihdevuosien aikana kuukautiset voivat muuttua runsaammiksi.",
      )}
    >
      <div role="group" aria-label={t("onboarding.lifeStage.legend", "Elämänvaihe")} className={s.options}>
        {LIFE_STAGES.map((stage) => (
          <ChoiceRow key={stage} role="checkbox" selected={value.includes(stage)} onSelect={() => toggle(stage)}>
            {optionLabels[stage]}
          </ChoiceRow>
        ))}
        <ChoiceRow role="checkbox" selected={none} onSelect={toggleNone}>
          {t("onboarding.lifeStage.none", "Mikään näistä ei koske minua")}
        </ChoiceRow>
      </div>
    </StepLayout>
  );
}
